// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  customer/src/context/CartContext.jsx
//  Copy to: C:\quickry\customer\src\context\CartContext.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  // Cart: array of { _id, name, price, qty, isVeg, image, restaurantId, restaurantName, storeId, storeName, serviceType }
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem("qr_cart");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem("qr_cart", JSON.stringify(cart));
  }, [cart]);

  // ── Add item to cart ──────────────────────────
  // If different restaurant — ask user (clear + add)
  const addItem = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i._id === item._id);
      if (existing) {
        return prev.map(i => i._id === item._id ? { ...i, qty: i.qty + 1 } : i);
      }

      // Different restaurant/store — clear cart first
      const prevVendorId = prev[0]?.restaurantId || prev[0]?.storeId;
      const newVendorId  = item.restaurantId || item.storeId;
      const prevName     = prev[0]?.restaurantName || prev[0]?.storeName || "current store";
      const newName      = item.restaurantName || item.storeName || "new store";

      if (prev.length > 0 && prevVendorId !== newVendorId) {
        const ok = window.confirm(
          `Your cart has items from "${prevName}".\n\nStart fresh order from "${newName}"?`
        );
        if (!ok) return prev;
        return [{ ...item, qty: 1 }];
      }

      return [...prev, { ...item, qty: 1 }];
    });
  };

  // ── Update qty (0 = remove) ───────────────────
  const updateQty = (itemId, qty) => {
    if (qty <= 0) {
      setCart(prev => prev.filter(i => i._id !== itemId && i.id !== itemId));
    } else {
      setCart(prev =>
        prev.map(i =>
          (i._id === itemId || i.id === itemId) ? { ...i, qty } : i
        )
      );
    }
  };

  // ── Remove item ───────────────────────────────
  const removeItem = (itemId) => {
    setCart(prev => prev.filter(i => i._id !== itemId && i.id !== itemId));
  };

  // ── Clear cart ────────────────────────────────
  const clearCart = () => setCart([]);

  // ── Derived values ────────────────────────────
  const itemCount  = cart.reduce((s, i) => s + i.qty, 0);
  const totalAmount= cart.reduce((s, i) => s + i.price * i.qty, 0);

  // Legacy support
  const addToCart    = addItem;
  const increaseQty  = (id) => updateQty(id, (cart.find(i=>i._id===id)?.qty||0)+1);
  const decreaseQty  = (id) => updateQty(id, (cart.find(i=>i._id===id)?.qty||0)-1);
  const total        = itemCount;
  const amount       = totalAmount;

  return (
    <CartContext.Provider value={{
      cart, addItem, updateQty, removeItem, clearCart,
      itemCount, totalAmount,
      // legacy
      addToCart, increaseQty, decreaseQty, total, amount,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
}

export default CartContext;